import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Send, Download, ArrowLeft, TrendingUp, Medal, XCircle, Bell, CheckCircle, Clock, Copy, Trash2, CalendarPlus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';


const RFQDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recommendedSuppliers, setRecommendedSuppliers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCategories, setSelectedCategories] = useState([]); // Multi-category filter
  const [categories, setCategories] = useState([]);
  const [sentSuppliers, setSentSuppliers] = useState([]); // Suppliers who already received this RFQ
  const [sendingReminder, setSendingReminder] = useState(null); // Track which supplier is receiving reminder
  const [sendingRFQ, setSendingRFQ] = useState(false); // Track send RFQ button state
  const [copying, setCopying] = useState(false);
  const [showAwardDialog, setShowAwardDialog] = useState(false);
  const [awardData, setAwardData] = useState({ supplierId: '', itemNum: 1, remark: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: '', itemId: '', itemName: '' });
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [extendingDeadline, setExtendingDeadline] = useState(false);

  useEffect(() => {
    fetchRFQDetail();
  }, [id]);

  const fetchRFQDetail = async () => {
    try {
      const [rfqRes, quotesRes, suppliersRes, categoriesRes] = await Promise.all([
        apiClient.get(`/rfqs/${id}`),
        apiClient.get(`/quotes/${id}`),
        apiClient.get('/suppliers'),
        apiClient.get('/suppliers/categories'),
      ]);

      setRfq(rfqRes.data);
      setQuotes(quotesRes.data);
      setSuppliers(suppliersRes.data);
      
      const fetchedCategories = categoriesRes.data.categories || [];
      console.log('Fetched categories:', fetchedCategories);
      setCategories(fetchedCategories);

      if (quotesRes.data.length > 0) {
        const comparisonRes = await apiClient.get(`/comparison/${id}`);
        setComparison(comparisonRes.data);
      }
      
      // Fetch suppliers who already received this RFQ
      try {
        const sentRes = await apiClient.get(`/rfqs/${id}/sent-suppliers`);
        setSentSuppliers(sentRes.data);
      } catch (e) {
        console.log('Could not fetch sent suppliers');
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching RFQ detail:', error);
      toast.error('Failed to load RFQ details');
      setLoading(false);
    }
  };

  const fetchRecommended = async (productTypes) => {
    if (!productTypes || productTypes.length === 0) return;
    try {
      const res = await apiClient.get(`/suppliers/recommended/${productTypes[0]}`);
      setRecommendedSuppliers(res.data);
    } catch (error) {
      console.error('Error fetching recommended suppliers:', error);
    }
  };

  const handleSendRFQ = async () => {
    if (selectedSuppliers.length === 0) {
      toast.error('Please select at least one supplier');
      return;
    }
    
    if (sendingRFQ) return; // Prevent double-click
    
    setSendingRFQ(true);
    try {
      await apiClient.post(`/rfqs/${id}/send`, {
        supplier_ids: selectedSuppliers,
      });
      toast.success(`RFQ sent to ${selectedSuppliers.length} supplier(s)`);
      setShowSendDialog(false);
      setSelectedSuppliers([]);
      fetchRFQDetail();
    } catch (error) {
      console.error('Error sending RFQ:', error);
      toast.error(error.response?.data?.detail || 'Failed to send RFQ. Please check email configuration.');
    } finally {
      setSendingRFQ(false);
    }
  };

  const handleSendReminder = async (supplierId) => {
    setSendingReminder(supplierId);
    try {
      await apiClient.post(`/rfqs/${id}/send-reminder/${supplierId}`);
      toast.success('Reminder sent successfully');
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
    setSendingReminder(null);
  };

  // Helper to check if a supplier has already received this RFQ
  const getSupplierStatus = (supplierId) => {
    const sent = sentSuppliers.find(s => s.supplier_id === supplierId);
    if (!sent) return null;
    return {
      sent: true,
      submitted: sent.status === 'submitted',
      sentAt: sent.sent_at
    };
  };

  const handleExport = async (format) => {
    try {
      const response = await apiClient.get(`/comparison/${id}/export?format=${format}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comparison_${rfq.rfq_number}.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Comparison exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export comparison');
    }
  };

  const toggleSupplier = (supplierId) => {
    setSelectedSuppliers((prev) =>
      prev.includes(supplierId) ? prev.filter((id) => id !== supplierId) : [...prev, supplierId]
    );
  };

  const handleOpenSendDialog = () => {
    if (suppliers.length === 0) {
      toast.error('No suppliers available. Please add suppliers first.');
      return;
    }
    setShowSendDialog(true);
  };

  const handleCloseRFQ = async () => {
    if (!window.confirm('Are you sure you want to close this RFQ? No more quotes will be accepted.')) {
      return;
    }

    try {
      await apiClient.post(`/rfqs/${id}/close`);
      toast.success('RFQ closed successfully');
      fetchRFQDetail();
    } catch (error) {
      console.error('Error closing RFQ:', error);
      toast.error('Failed to close RFQ');
    }
  };

  const handleExtendDeadline = async () => {
    if (extendDays < 1) {
      toast.error('Please enter at least 1 day');
      return;
    }
    
    setExtendingDeadline(true);
    try {
      const response = await apiClient.post(`/rfqs/${id}/extend-deadline`, {
        additional_days: extendDays
      });
      toast.success(response.data.message);
      setShowExtendDialog(false);
      setExtendDays(7);
      fetchRFQDetail();
    } catch (error) {
      console.error('Error extending deadline:', error);
      toast.error(error.response?.data?.detail || 'Failed to extend deadline');
    } finally {
      setExtendingDeadline(false);
    }
  };

  const handleAwardOrder = async (supplierId, itemNum = 1) => {
    // Open the award dialog instead of direct confirm
    setAwardData({ supplierId, itemNum, remark: '' });
    setShowAwardDialog(true);
  };

  const confirmAwardOrder = async () => {
    const { supplierId, itemNum, remark } = awardData;
    const itemLabel = itemNum === 1 ? 'Item 1 (Primary)' : `Item ${itemNum}`;
    
    try {
      const response = await apiClient.post(`/rfqs/${id}/award/${supplierId}?item_num=${itemNum}&remark=${encodeURIComponent(remark)}`);
      toast.success(`${itemLabel} awarded to ${response.data.supplier.name}`);
      setShowAwardDialog(false);
      setAwardData({ supplierId: '', itemNum: 1, remark: '' });
      fetchRFQDetail();
    } catch (error) {
      console.error('Error awarding order:', error);
      toast.error(error.response?.data?.detail || 'Failed to award order');
    }
  };

  // Copy RFQ functionality
  const handleCopyRFQ = async () => {
    setCopying(true);
    try {
      const response = await apiClient.post(`/rfqs/${id}/copy`);
      toast.success(`RFQ copied! New RFQ: ${response.data.rfq.rfq_number}`);
      // Navigate to the new RFQ
      navigate(`/rfqs/${response.data.rfq.id}`);
    } catch (error) {
      console.error('Error copying RFQ:', error);
      toast.error(error.response?.data?.detail || 'Failed to copy RFQ');
    } finally {
      setCopying(false);
    }
  };

  // Delete item from RFQ (only for unsent RFQs)
  const handleDeleteItem = (itemId, itemName) => {
    console.log('handleDeleteItem called:', itemId, itemName);
    setDeleteTarget({ type: 'item', itemId, itemName });
    setShowDeleteConfirm(true);
  };

  // Delete entire RFQ (only for unsent RFQs)
  const handleDeleteRFQ = () => {
    console.log('handleDeleteRFQ called');
    setDeleteTarget({ type: 'rfq', itemId: '', itemName: '' });
    setShowDeleteConfirm(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    
    if (deleteTarget.type === 'item') {
      try {
        console.log('Calling delete API for item:', deleteTarget.itemId);
        await apiClient.delete(`/rfqs/${id}/items/${deleteTarget.itemId}`);
        toast.success(`Item "${deleteTarget.itemName}" removed successfully`);
        fetchRFQDetail();
      } catch (error) {
        console.error('Error deleting item:', error);
        toast.error(error.response?.data?.detail || 'Failed to remove item');
      }
    } else if (deleteTarget.type === 'rfq') {
      try {
        console.log('Calling delete API for RFQ:', id);
        await apiClient.delete(`/rfqs/${id}`);
        toast.success('RFQ deleted successfully');
        navigate('/rfqs');
      } catch (error) {
        console.error('Error deleting RFQ:', error);
        toast.error(error.response?.data?.detail || 'Failed to delete RFQ');
      }
    }
  };

  // Helper to check if an item is already awarded
  const getItemAward = (itemNum) => {
    return (rfq?.item_awards || []).find(a => a.item_num === itemNum);
  };

  const handleViewPO = async (supplierId, itemNum = 1) => {
    try {
      const response = await apiClient.get(`/rfqs/${id}/purchase-order/${supplierId}?item_num=${itemNum}`, {
        responseType: 'blob',
      });
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Clean up the blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error('Error generating PO:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate Purchase Order');
    }
  };

  const toggleSelectAll = () => {
    const filteredSuppliers = getFilteredSuppliers();
    if (selectedSuppliers.length === filteredSuppliers.length) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(filteredSuppliers.map(s => s.id));
    }
  };

  const getFilteredSuppliers = () => {
    // Multi-category filter support - check both 'category' and 'categories' array
    if (selectedCategories.length > 0) {
      return suppliers.filter(s => {
        // Handle "Uncategorized" selection
        if (selectedCategories.includes('Uncategorized')) {
          const supplierCategories = s.categories || (s.category ? [s.category] : []);
          if (supplierCategories.length === 0 || !s.category) {
            return true;
          }
        }
        // Check if supplier has any of the selected categories
        const supplierCategories = s.categories || (s.category ? [s.category] : []);
        return selectedCategories.some(cat => cat !== 'Uncategorized' && supplierCategories.includes(cat));
      });
    }
    if (selectedCategory === 'all') {
      return suppliers;
    }
    // Handle "Uncategorized" single category
    if (selectedCategory === 'Uncategorized') {
      return suppliers.filter(s => {
        const supplierCategories = s.categories || (s.category ? [s.category] : []);
        return supplierCategories.length === 0 || !s.category;
      });
    }
    // Single category filter - check both 'category' and 'categories' array
    return suppliers.filter(s => {
      const supplierCategories = s.categories || (s.category ? [s.category] : []);
      return supplierCategories.includes(selectedCategory);
    });
  };

  // Toggle category selection for multi-select
  const toggleCategorySelection = (cat) => {
    if (selectedCategories.includes(cat)) {
      setSelectedCategories(selectedCategories.filter(c => c !== cat));
    } else {
      setSelectedCategories([...selectedCategories, cat]);
    }
    // Reset single category filter when using multi-select
    setSelectedCategory('all');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96" data-testid="rfq-detail-loading">Loading...</div>;
  }

  if (!rfq) {
    return <div className="flex items-center justify-center h-96" data-testid="rfq-not-found">RFQ not found</div>;
  }

  return (
    <div data-testid="rfq-detail-page">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/rfqs')} data-testid="back-to-rfqs-btn">
          <ArrowLeft size={18} />
          Back to RFQs
        </Button>
      </div>

      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="rfq-detail-title">
            {rfq.rfq_number}
          </h1>
          <p className="text-sm text-slate-500 mt-2">{rfq.item_name}</p>
        </div>
        <div className="flex gap-2">
          {rfq.status === 'draft' && (
            <Button onClick={handleOpenSendDialog} data-testid="send-rfq-btn">
              <Send size={18} />
              Send to Suppliers
            </Button>
          )}
          {rfq.status === 'sent' && (
            <>
              <Button onClick={handleOpenSendDialog} variant="outline" data-testid="resend-rfq-btn">
                <Send size={18} />
                Send to More Suppliers
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowExtendDialog(true)} 
                data-testid="extend-deadline-btn"
                className="border-green-600 text-green-600 hover:bg-green-50"
              >
                <CalendarPlus size={18} />
                Extend Deadline
              </Button>
              <Button variant="outline" onClick={handleCloseRFQ} data-testid="close-rfq-btn" className="border-red-600 text-red-600 hover:bg-red-50">
                <XCircle size={18} />
                Close RFQ
              </Button>
            </>
          )}
          {quotes.length > 0 && (
            <>
              <Button variant="outline" onClick={() => handleExport('excel')} data-testid="export-excel-btn">
                <Download size={18} />
                Export Excel
              </Button>
              <Button variant="outline" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
                <Download size={18} />
                Export PDF
              </Button>
            </>
          )}
          <Button 
            variant="outline" 
            onClick={handleCopyRFQ} 
            disabled={copying}
            data-testid="copy-rfq-btn"
          >
            <Copy size={18} />
            {copying ? 'Copying...' : 'Copy RFQ'}
          </Button>
          {(rfq.sent_count === 0 || rfq.sent_count === undefined) && rfq.status === 'draft' && (
            <Button 
              variant="outline" 
              onClick={handleDeleteRFQ}
              className="border-red-600 text-red-600 hover:bg-red-50"
              data-testid="delete-rfq-detail-btn"
            >
              <Trash2 size={18} />
              Delete RFQ
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6" data-testid="rfq-details-card">
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-4">RFQ Details</h2>
        
        {/* Primary Item (Item 1) */}
        <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg mb-4">
          <div className="flex justify-between items-start mb-3">
            <h3 className="text-lg font-semibold text-blue-800">📦 Item 1 (Primary)</h3>
            {rfq.status === 'draft' && (rfq.sent_count === 0 || rfq.sent_count === undefined) && rfq.items && rfq.items.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDeleteItem('item_1', rfq.item_name)}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="delete-item-1"
              >
                <XCircle size={14} className="mr-1" />
                Remove
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Item Name</p>
              <p className="text-sm font-medium text-slate-900">{rfq.item_name}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Quantity</p>
              <p className="text-sm font-mono font-medium text-slate-900">
                {rfq.quantity} {rfq.unit}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Description</p>
              <p className="text-sm text-slate-700">{rfq.description}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Payment Term</p>
              <p className="text-sm text-slate-700">{rfq.payment_term || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Transport</p>
              <p className="text-sm text-slate-700">{rfq.transport || 'Not specified'}</p>
            </div>
            
            {/* Item 1 Custom Fields (Mandatory Requirements) */}
            {rfq.custom_fields && rfq.custom_fields.filter(f => f.type !== 'file').length > 0 && (
              <div className="col-span-2 bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-800 uppercase tracking-wider mb-2 font-semibold">Mandatory Requirements</p>
                <div className="grid grid-cols-2 gap-3">
                  {rfq.custom_fields.filter(f => f.type !== 'file').map((field, idx) => (
                    <div key={idx}>
                      <p className="text-xs text-slate-500 mb-1">{field.label}</p>
                      <p className="text-sm font-medium text-slate-900">
                        {field.type === 'dropdown' ? `Options: ${(field.options || []).join(', ')}` : (field.value || 'Supplier to provide')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Item 1 Attachments */}
            {rfq.attachments && rfq.attachments.length > 0 && (
              <div className="col-span-2 bg-white p-3 rounded-lg border border-blue-200">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Attachments</p>
                <div className="flex flex-wrap gap-2">
                  {rfq.attachments.map((att, idx) => (
                    <a
                      key={idx}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/rfqs/${rfq.id}/download/${att.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-md text-sm hover:bg-blue-200"
                    >
                      📎 {att.filename}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Additional Items (Item 2, 3, etc.) */}
        {rfq.items && rfq.items.length > 0 && rfq.items.map((item, index) => (
          <div key={item.id} className="p-4 bg-green-50 border-2 border-green-200 rounded-lg mb-4">
            <div className="flex justify-between items-start mb-3">
              <h3 className="text-lg font-semibold text-green-800">📦 Item {index + 2}</h3>
              {rfq.status === 'draft' && (rfq.sent_count === 0 || rfq.sent_count === undefined) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteItem(item.id, item.item_name)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid={`delete-item-${item.id}`}
                >
                  <XCircle size={14} className="mr-1" />
                  Remove
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Item Name</p>
                <p className="text-sm font-medium text-slate-900">{item.item_name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Quantity</p>
                <p className="text-sm font-mono font-medium text-slate-900">
                  {item.quantity} {item.unit}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Description</p>
                <p className="text-sm text-slate-700">{item.description || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Payment Term</p>
                <p className="text-sm text-slate-700">{item.payment_term || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Transport</p>
                <p className="text-sm text-slate-700">{item.transport || 'Not specified'}</p>
              </div>
              
              {/* Item-specific Custom Fields */}
              {item.custom_fields && item.custom_fields.length > 0 && (
                <div className="col-span-2 bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-800 uppercase tracking-wider mb-2 font-semibold">Mandatory Requirements for Item {index + 2}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {item.custom_fields.filter(f => f.type !== 'file').map((field, idx) => (
                      <div key={idx}>
                        <p className="text-xs text-slate-500 mb-1">{field.label}</p>
                        <p className="text-sm font-medium text-slate-900">
                          {field.type === 'dropdown' ? `Options: ${(field.options || []).join(', ')}` : (field.value || 'Supplier to provide')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Item-specific Attachments */}
              {item.attachments && item.attachments.length > 0 && (
                <div className="col-span-2 bg-white p-3 rounded-lg border border-green-200">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Attachments</p>
                  <div className="flex flex-wrap gap-2">
                    {item.attachments.map((att, idx) => (
                      <a
                        key={idx}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/rfqs/${rfq.id}/download/${att.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-md text-sm hover:bg-green-200"
                      >
                        📎 {att.filename}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {/* General Info */}
        <div className="grid grid-cols-2 gap-6 pt-4 border-t">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Status</p>
            <span
              className={`badge ${
                rfq.status === 'sent' ? 'badge-info' : rfq.status === 'completed' ? 'badge-success' : 'badge-warning'
              }`}
            >
              {rfq.status}
            </span>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Link Validity</p>
            <p className="text-sm font-medium text-slate-900">{rfq.link_validity_days} days</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Total Items</p>
            <p className="text-sm font-medium text-slate-900">{1 + (rfq.items?.length || 0)}</p>
          </div>
        </div>
      </div>

      {quotes.length > 0 && comparison && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm mb-6" data-testid="quotes-comparison">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Quote Comparison</h2>
            <p className="text-sm text-slate-500 mt-1">
              {quotes.length} quote(s) received | {1 + (rfq.items?.length || 0)} item(s) in RFQ
            </p>
          </div>
          
          {/* ==================== ITEM 1 SECTION ==================== */}
          <div className="border-b-4 border-blue-400">
            <div className="p-4 bg-blue-50 border-b-2 border-blue-200">
              <div className="flex flex-wrap items-baseline gap-2">
                <h3 className="text-lg font-semibold text-blue-900">📦 Item 1 (Primary): {rfq.item_name}</h3>
                {rfq.description && (
                  <span className="text-sm text-blue-600 italic">- {rfq.description}</span>
                )}
              </div>
              <p className="text-sm text-blue-700 mt-1">Qty: {rfq.quantity} {rfq.unit}</p>
            </div>
            
            {/* Item 1 Comparison Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Rank</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Supplier</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Rate</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase" title="Minimum Order Quantity">MOQ (Min Order Qty)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Delivery</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Terms</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {comparison.quotes.map((quote, idx) => {
                    const isNotAvailable = quote.item_not_available;
                    // Only count available quotes for ranking
                    const availableQuotesBefore = comparison.quotes.slice(0, idx).filter(q => !q.item_not_available);
                    const rankNum = availableQuotesBefore.length + 1;
                    const rank = isNotAvailable ? '-' : (rankNum === 1 ? 'L1' : rankNum === 2 ? 'L2' : rankNum === 3 ? 'L3' : `L${rankNum}`);
                    const isL1 = !isNotAvailable && rankNum === 1;
                    return (
                      <tr key={quote.id} className={isNotAvailable ? 'bg-slate-100 opacity-70' : (isL1 ? 'l1-row' : 'hover:bg-slate-50')} data-testid={`quote-row-${quote.id}`}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {isL1 && <Medal className="text-yellow-600" size={16} />}
                            <span className={`badge ${isNotAvailable ? 'badge-warning' : (isL1 ? 'l1-badge' : 'badge-info')}`}>{rank}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-slate-900">{quote.supplier.name}</div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isNotAvailable ? (
                            <span className="text-sm text-orange-600 font-medium italic">NOT AVAILABLE</span>
                          ) : (
                            <span className="font-mono font-semibold text-slate-900">{quote.rate.toFixed(2)}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-mono text-sm">{isNotAvailable ? '-' : quote.moq}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm">{isNotAvailable ? '-' : quote.delivery_time}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-700">{isNotAvailable ? '-' : (quote.terms || '-')}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex gap-2">
                            {isNotAvailable ? (
                              <span className="text-xs text-slate-400 italic">Item not available</span>
                            ) : (
                              (() => {
                                const item1Award = getItemAward(1);
                                if (item1Award) {
                                  if (item1Award.supplier_id === quote.supplier_id) {
                                    return (
                                      <>
                                        <span className="badge badge-success text-xs">✓ Awarded</span>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleViewPO(quote.supplier_id, 1)}
                                          data-testid={`view-po-${quote.id}`}
                                          className="text-xs"
                                        >
                                          View PO
                                        </Button>
                                      </>
                                    );
                                  }
                                  return <span className="text-xs text-slate-400">-</span>;
                                }
                                return (
                                  <Button
                                    size="sm"
                                    onClick={() => handleAwardOrder(quote.supplier_id, 1)}
                                    data-testid={`award-order-${quote.id}`}
                                    className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                  >
                                    Award Item 1
                                  </Button>
                                );
                              })()
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Item 1 - Mandatory Requirement Responses (directly below Item 1 table) */}
            {rfq.custom_fields && rfq.custom_fields.filter(f => f.type !== 'file').length > 0 && 
             comparison.quotes.some(q => q.rfq_custom_field_responses && q.rfq_custom_field_responses.length > 0) && (
              <div className="p-4 bg-amber-50 border-t border-amber-200">
                <h4 className="text-md font-semibold text-amber-900 mb-3">Item 1 - Mandatory Requirement Responses</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-amber-100">
                        <th className="px-3 py-2 text-left font-semibold text-amber-900 text-xs">Requirement</th>
                        {comparison.quotes.map((quote, idx) => {
                          const rank = idx === 0 ? 'L1' : idx === 1 ? 'L2' : idx === 2 ? 'L3' : `L${idx + 1}`;
                          return (
                            <th key={quote.id} className="px-3 py-2 text-left font-semibold text-amber-900 text-xs">
                              <span className={`badge ${idx === 0 ? 'l1-badge' : 'badge-info'} mr-1 text-xs`}>{rank}</span>
                              {quote.supplier.name}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {rfq.custom_fields.filter(f => f.type !== 'file').map((field) => (
                        <tr key={field.id || field.label} className="border-b border-amber-200">
                          <td className="px-3 py-2 font-medium text-slate-900 bg-white text-xs">{field.label}</td>
                          {comparison.quotes.map(quote => {
                            const response = (quote.rfq_custom_field_responses || []).find(
                              r => r.field_id === field.id || r.field_id === field.label || r.label === field.label
                            );
                            return (
                              <td key={quote.id} className="px-3 py-2 text-slate-700 bg-white text-xs">
                                {response?.value || '-'}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Item 1 - Supplier Attachments (general quote attachments) */}
            {comparison.quotes.some(q => q.attachments && q.attachments.length > 0) && (
              <div className="p-4 bg-blue-50 border-t border-blue-200">
                <h4 className="text-md font-semibold text-blue-900 mb-3">Item 1 - Supplier Attachments</h4>
                <div className="space-y-2">
                  {comparison.quotes.map((quote, idx) => {
                    if (!quote.attachments || quote.attachments.length === 0) return null;
                    const rank = idx === 0 ? 'L1' : idx === 1 ? 'L2' : idx === 2 ? 'L3' : `L${idx + 1}`;
                    // Deduplicate attachments by ID
                    const uniqueAttachments = quote.attachments.filter((att, index, self) => 
                      index === self.findIndex(a => a.id === att.id)
                    );
                    return (
                      <div key={quote.id} className="flex items-center gap-2 p-2 bg-white rounded border border-blue-100">
                        <span className={`badge ${idx === 0 ? 'l1-badge' : 'badge-info'} text-xs`}>{rank}</span>
                        <span className="text-sm font-medium text-slate-700">{quote.supplier.name}:</span>
                        <div className="flex flex-wrap gap-1">
                          {uniqueAttachments.map((att) => (
                            <a
                              key={att.id}
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/download-attachment/${att.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            >
                              📎 {att.filename}
                            </a>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          {/* ==================== ADDITIONAL ITEMS (Item 2, 3, etc.) ==================== */}
          {rfq.items && rfq.items.length > 0 && rfq.items.map((item, itemIdx) => {
            // Sort quotes by this item's rate - exclude not available items from ranking
            const sortedQuoteData = comparison.quotes
              .map(quote => {
                const itemQuote = (quote.item_quotes || []).find(q => q.item_id === item.id);
                return { quote, itemQuote };
              })
              .sort((a, b) => {
                const aNotAvailable = a.itemQuote?.item_not_available || false;
                const bNotAvailable = b.itemQuote?.item_not_available || false;
                // Not available items go to the end
                if (aNotAvailable && !bNotAvailable) return 1;
                if (!aNotAvailable && bNotAvailable) return -1;
                // Both available or both not available - sort by rate
                return (a.itemQuote?.rate || 9999999) - (b.itemQuote?.rate || 9999999);
              });
            
            const itemFields = (item.custom_fields || []).filter(f => f.type !== 'file');
            
            return (
              <div key={item.id} className="border-b-4 border-green-400">
                <div className="p-4 bg-green-50 border-b-2 border-green-200">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <h3 className="text-lg font-semibold text-green-900">📦 Item {itemIdx + 2}: {item.item_name}</h3>
                    {item.description && (
                      <span className="text-sm text-green-600 italic">- {item.description}</span>
                    )}
                  </div>
                  <p className="text-sm text-green-700 mt-1">Qty: {item.quantity} {item.unit}</p>
                </div>
                
                {/* Item Comparison Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Rank</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Supplier</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Rate</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase" title="Minimum Order Quantity">MOQ (Min Order Qty)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Delivery</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Terms</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {(() => {
                        let rankCounter = 0;
                        return sortedQuoteData.map(({ quote, itemQuote }, idx) => {
                          const isNotAvailable = itemQuote?.item_not_available || false;
                          if (!isNotAvailable) rankCounter++;
                          const rank = isNotAvailable ? '-' : (rankCounter === 1 ? 'L1' : rankCounter === 2 ? 'L2' : rankCounter === 3 ? 'L3' : `L${rankCounter}`);
                          const isL1 = !isNotAvailable && rankCounter === 1;
                          // Get terms from item quote or fall back to main quote terms
                          const itemTerms = itemQuote?.terms || quote.terms || '-';
                          return (
                            <tr key={quote.id} className={isNotAvailable ? 'bg-slate-100 opacity-70' : (isL1 ? 'bg-yellow-50' : 'hover:bg-slate-50')}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  {isL1 && <Medal className="text-yellow-600" size={16} />}
                                  <span className={`badge ${isNotAvailable ? 'badge-warning' : (isL1 ? 'l1-badge' : 'badge-info')}`}>{rank}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-slate-900">{quote.supplier.name}</div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                {isNotAvailable ? (
                                  <span className="text-sm text-orange-600 font-medium italic">NOT AVAILABLE</span>
                                ) : (
                                  <span className="font-mono font-semibold">
                                    {itemQuote?.rate?.toFixed(2) || '-'}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="font-mono text-sm">{isNotAvailable ? '-' : (itemQuote?.moq || '-')}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm">{isNotAvailable ? '-' : (itemQuote?.delivery_time || '-')}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm text-slate-700">{isNotAvailable ? '-' : itemTerms}</span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex gap-2">
                                  {isNotAvailable ? (
                                    <span className="text-xs text-slate-400 italic">Item not available</span>
                                  ) : (
                                    (() => {
                                      const itemAward = getItemAward(itemIdx + 2);
                                      if (itemAward) {
                                        if (itemAward.supplier_id === quote.supplier_id) {
                                          return (
                                            <>
                                              <span className="badge badge-success text-xs">✓ Awarded</span>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleViewPO(quote.supplier_id, itemIdx + 2)}
                                                data-testid={`view-po-item${itemIdx+2}-${quote.id}`}
                                                className="text-xs"
                                              >
                                                View PO
                                              </Button>
                                            </>
                                          );
                                        }
                                        return <span className="text-xs text-slate-400">-</span>;
                                      }
                                      return (
                                        <Button
                                          size="sm"
                                          onClick={() => handleAwardOrder(quote.supplier_id, itemIdx + 2)}
                                          data-testid={`award-item${itemIdx+2}-${quote.id}`}
                                          className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                        >
                                          Award Item {itemIdx + 2}
                                        </Button>
                                      );
                                    })()
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
                
                {/* Item-specific Mandatory Requirement Responses (directly below item table) */}
                {itemFields.length > 0 && (
                  <div className="p-4 bg-amber-50 border-t border-amber-200">
                    <h4 className="text-md font-semibold text-amber-900 mb-3">Item {itemIdx + 2} - Mandatory Requirement Responses</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-amber-100">
                            <th className="px-3 py-2 text-left font-semibold text-amber-900 text-xs">Requirement</th>
                            {sortedQuoteData.map(({ quote }, idx) => {
                              const rank = idx === 0 ? 'L1' : idx === 1 ? 'L2' : idx === 2 ? 'L3' : `L${idx + 1}`;
                              return (
                                <th key={quote.id} className="px-3 py-2 text-left font-semibold text-amber-900 text-xs">
                                  <span className={`badge ${idx === 0 ? 'l1-badge' : 'badge-info'} mr-1 text-xs`}>{rank}</span>
                                  {quote.supplier.name}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {itemFields.map((field) => (
                            <tr key={field.id || field.label} className="border-b border-amber-200">
                              <td className="px-3 py-2 font-medium text-slate-900 bg-white text-xs">{field.label}</td>
                              {sortedQuoteData.map(({ quote, itemQuote }) => {
                                const response = itemQuote 
                                  ? (itemQuote.custom_field_responses || []).find(
                                      r => r.field_id === field.id || r.field_id === field.label || r.label === field.label
                                    )
                                  : null;
                                return (
                                  <td key={quote.id} className="px-3 py-2 text-slate-700 bg-white text-xs">
                                    {response?.value || '-'}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Item-specific Supplier Attachments */}
                {sortedQuoteData.some(({ itemQuote }) => itemQuote?.attachments && itemQuote.attachments.length > 0) && (
                  <div className="p-4 bg-blue-50 border-t border-blue-200">
                    <h4 className="text-md font-semibold text-blue-900 mb-3">Item {itemIdx + 2} - Supplier Attachments</h4>
                    <div className="space-y-2">
                      {sortedQuoteData.map(({ quote, itemQuote }, idx) => {
                        if (!itemQuote?.attachments || itemQuote.attachments.length === 0) return null;
                        const rank = idx === 0 ? 'L1' : idx === 1 ? 'L2' : idx === 2 ? 'L3' : `L${idx + 1}`;
                        return (
                          <div key={quote.id} className="flex items-center gap-2 p-2 bg-white rounded border border-blue-100">
                            <span className={`badge ${idx === 0 ? 'l1-badge' : 'badge-info'} text-xs`}>{rank}</span>
                            <span className="text-sm font-medium text-slate-700">{quote.supplier.name}:</span>
                            <div className="flex flex-wrap gap-1">
                              {itemQuote.attachments.map((att) => (
                                <a
                                  key={att.id}
                                  href={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/download-attachment/${att.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                >
                                  📎 {att.filename}
                                </a>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Supplier Custom Fields Section */}
          {comparison.quotes.some(q => q.supplier_custom_fields && q.supplier_custom_fields.length > 0) && (
            <div className="p-6 border-t border-slate-200 bg-slate-50">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Supplier Additional Information</h3>
              <div className="space-y-4">
                {comparison.quotes.map((quote, idx) => {
                  if (!quote.supplier_custom_fields || quote.supplier_custom_fields.length === 0) return null;
                  const rank = idx === 0 ? 'L1' : idx === 1 ? 'L2' : idx === 2 ? 'L3' : `L${idx + 1}`;
                  return (
                    <div key={quote.id} className="bg-white p-4 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`badge ${idx === 0 ? 'l1-badge' : 'badge-info'}`}>{rank}</span>
                        <span className="font-medium text-slate-900">{quote.supplier.name}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {quote.supplier_custom_fields.map((field, fieldIdx) => (
                          <div key={fieldIdx}>
                            <p className="text-xs text-slate-500 mb-1">{field.label}</p>
                            <p className="text-sm font-medium text-slate-900">{field.value || 'N/A'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {quotes.length === 0 && rfq.status === 'sent' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center" data-testid="awaiting-quotes">
          <p className="text-blue-900 font-medium">Awaiting quotes from suppliers</p>
          <p className="text-sm text-blue-700 mt-1">You will be notified when suppliers submit their quotations.</p>
        </div>
      )}

      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="max-w-2xl" data-testid="send-rfq-dialog">
          <DialogHeader>
            <DialogTitle>Send RFQ to Suppliers</DialogTitle>
            <DialogDescription>Select suppliers to send this RFQ. They will receive an email with a link to submit their quotation.</DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col gap-4 mb-4">
            {/* Multi-Category Filter */}
            <div>
              <Label className="mb-2 block">Filter by Category (select multiple)</Label>
              <div className="flex flex-wrap gap-2 p-2 border border-slate-200 rounded-md bg-slate-50 max-h-24 overflow-y-auto">
                {categories.length === 0 ? (
                  <span className="text-xs text-slate-500">No categories found - Add category to suppliers first</span>
                ) : (
                  <>
                    {categories.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleCategorySelection(cat)}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          selectedCategories.includes(cat)
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'
                        }`}
                        data-testid={`category-chip-${cat}`}
                      >
                        {cat}
                      </button>
                    ))}
                    {/* Uncategorized option */}
                    <button
                      type="button"
                      onClick={() => toggleCategorySelection('Uncategorized')}
                      className={`px-3 py-1 text-xs rounded-full transition-colors ${
                        selectedCategories.includes('Uncategorized')
                          ? 'bg-slate-600 text-white'
                          : 'bg-white border border-slate-300 text-slate-500 hover:bg-slate-100 italic'
                      }`}
                      data-testid="category-chip-uncategorized"
                    >
                      Uncategorized
                    </button>
                  </>
                )}
              </div>
              {selectedCategories.length > 0 && (
                <p className="text-xs text-blue-600 mt-1">
                  Selected: {selectedCategories.join(', ')}
                  <button 
                    type="button"
                    onClick={() => setSelectedCategories([])} 
                    className="ml-2 text-red-500 hover:underline"
                  >
                    Clear
                  </button>
                </p>
              )}
            </div>
            
            {/* Legacy Single Category Dropdown - still available */}
            <div className="flex gap-4 items-center">
              <div className="flex-1">
                <Label htmlFor="category-filter">Or filter by single category</Label>
                <select
                  id="category-filter"
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md"
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setSelectedCategories([]); // Clear multi-select when using dropdown
                  }}
                  data-testid="category-filter"
                >
                  <option value="all">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value="Uncategorized">Uncategorized</option>
                </select>
              </div>
              <div className="pt-6">
                <Button
                  variant="outline"
                  onClick={toggleSelectAll}
                  data-testid="select-all-btn"
                >
                  {selectedSuppliers.length === getFilteredSuppliers().length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
            </div>
          </div>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {getFilteredSuppliers().map((supplier) => {
              const status = getSupplierStatus(supplier.id);
              const hasSubmitted = status?.submitted;
              const hasSent = status?.sent;
              
              return (
              <div
                key={supplier.id}
                className={`flex items-start gap-3 p-3 border rounded-lg ${
                  hasSent 
                    ? hasSubmitted 
                      ? 'border-green-300 bg-green-50' 
                      : 'border-amber-300 bg-amber-50'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`supplier-checkbox-${supplier.id}`}
              >
                {!hasSent ? (
                  <Checkbox
                    checked={selectedSuppliers.includes(supplier.id)}
                    onCheckedChange={() => toggleSupplier(supplier.id)}
                    id={`supplier-${supplier.id}`}
                  />
                ) : (
                  <div className="w-5 h-5 flex items-center justify-center">
                    {hasSubmitted ? (
                      <CheckCircle size={18} className="text-green-600" />
                    ) : (
                      <Clock size={18} className="text-amber-600" />
                    )}
                  </div>
                )}
                <label htmlFor={`supplier-${supplier.id}`} className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-900">{supplier.name}</p>
                    {supplier.l1_count > 0 && (
                      <span className="badge badge-success text-xs flex items-center gap-1">
                        <TrendingUp size={12} />
                        L1 x{supplier.l1_count}
                      </span>
                    )}
                    {hasSent && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        hasSubmitted 
                          ? 'bg-green-200 text-green-800' 
                          : 'bg-amber-200 text-amber-800'
                      }`}>
                        {hasSubmitted ? 'Quote Submitted' : 'Awaiting Quote'}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    <span>{supplier.email}</span>
                    {supplier.email2 && (
                      <span className="ml-1 text-slate-400">| {supplier.email2}</span>
                    )}
                  </div>
                  {/* Product Types */}
                  {supplier.product_types && supplier.product_types.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className="text-xs text-slate-400 mr-1">Products:</span>
                      {supplier.product_types.map((type, idx) => (
                        <span key={idx} className="px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700">
                          {type}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Categories */}
                  {((supplier.categories && supplier.categories.length > 0) || supplier.category) && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className="text-xs text-slate-400 mr-1">Categories:</span>
                      {(supplier.categories && supplier.categories.length > 0) ? (
                        supplier.categories.map((cat, idx) => (
                          <span key={idx} className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                            {cat}
                          </span>
                        ))
                      ) : supplier.category ? (
                        <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                          {supplier.category}
                        </span>
                      ) : null}
                    </div>
                  )}
                </label>
                {/* Send Reminder Button - only for sent but not submitted */}
                {hasSent && !hasSubmitted && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSendReminder(supplier.id)}
                    disabled={sendingReminder === supplier.id}
                    className="text-xs h-8 border-amber-400 text-amber-700 hover:bg-amber-100"
                  >
                    <Bell size={14} className="mr-1" />
                    {sendingReminder === supplier.id ? 'Sending...' : 'Remind'}
                  </Button>
                )}
              </div>
              );
            })}
          </div>
          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowSendDialog(false)} data-testid="cancel-send-btn">
              Cancel
            </Button>
            <Button onClick={handleSendRFQ} disabled={selectedSuppliers.length === 0 || sendingRFQ} data-testid="confirm-send-btn">
              {sendingRFQ ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send to {selectedSuppliers.length} Supplier(s)
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Award Dialog with Remark */}
      <Dialog open={showAwardDialog} onOpenChange={setShowAwardDialog}>
        <DialogContent className="max-w-md" data-testid="award-dialog">
          <DialogHeader>
            <DialogTitle>Award Item to Supplier</DialogTitle>
            <DialogDescription>
              Confirm awarding {awardData.itemNum === 1 ? 'Item 1 (Primary)' : `Item ${awardData.itemNum}`} to this supplier.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="award-remark">Award Remark (Optional)</Label>
              <Textarea
                id="award-remark"
                placeholder="Add any notes or remarks for this award (e.g., negotiated terms, special conditions)..."
                value={awardData.remark}
                onChange={(e) => setAwardData({ ...awardData, remark: e.target.value })}
                rows={3}
                className="mt-1"
                data-testid="award-remark-input"
              />
              <p className="text-xs text-slate-500 mt-1">This remark will appear on the Purchase Order.</p>
            </div>
          </div>
          
          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowAwardDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmAwardOrder} className="bg-green-600 hover:bg-green-700" data-testid="confirm-award-btn">
              Confirm Award
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {deleteTarget.type === 'rfq' ? '⚠️ Delete RFQ' : '⚠️ Remove Item'}
            </DialogTitle>
            <DialogDescription>
              {deleteTarget.type === 'rfq' 
                ? 'Are you sure you want to delete this RFQ? This action cannot be undone.'
                : `Are you sure you want to remove "${deleteTarget.itemName}" from this RFQ?`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmDelete} 
              className="bg-red-600 hover:bg-red-700 text-white" 
              data-testid="confirm-delete-btn"
            >
              {deleteTarget.type === 'rfq' ? 'Delete RFQ' : 'Remove Item'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Extend Deadline Dialog */}
      <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
        <DialogContent className="max-w-md" data-testid="extend-deadline-dialog">
          <DialogHeader>
            <DialogTitle className="text-green-700 flex items-center gap-2">
              <CalendarPlus size={20} />
              Extend RFQ Deadline
            </DialogTitle>
            <DialogDescription>
              Extend the deadline for suppliers to submit their quotes. All suppliers who received this RFQ will be notified via email.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Current Validity:</strong> {rfq?.link_validity_days || 7} days
              </p>
              {rfq?.deadline_extended_at && (
                <p className="text-xs text-blue-600 mt-1">
                  Last extended: {new Date(rfq.deadline_extended_at).toLocaleDateString()}
                </p>
              )}
            </div>
            
            <div>
              <Label htmlFor="extend-days" className="text-sm font-medium">
                Add Additional Days
              </Label>
              <Input
                id="extend-days"
                type="number"
                min="1"
                max="30"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 1)}
                className="mt-1"
                data-testid="extend-days-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                New total validity: {(rfq?.link_validity_days || 7) + extendDays} days
              </p>
            </div>
          </div>
          
          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowExtendDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExtendDeadline}
              disabled={extendingDeadline}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="confirm-extend-btn"
            >
              {extendingDeadline ? 'Extending...' : 'Extend & Notify Suppliers'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RFQDetail;